<template>
  <!-- On a phone: the prototype's Q&A sheet (sheetQA). -->
  <div v-if="phoneQa" class="qna-phone" data-testid="qa-phone">
    <div v-if="isAdminOrSpeaker && settingsLoaded" class="qp-host">
      <XlSwitch
          :model-value="settings.takingQuestions"
          label="Taking questions"
          :label-first="false"
          data-testid="qa-taking"
          @change="toggleSetting('takingQuestions')"
      />
    </div>

    <div class="qp-body">
      <div v-if="!loaded" class="widget-loader">
        <div class="shimmer card-shimmer"></div>
        <div class="shimmer card-shimmer"></div>
      </div>

      <div v-else-if="!phoneAll.length" class="qp-empty" data-testid="qa-empty">
        <span class="qp-empty__g" aria-hidden="true">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M9.5 9.3a2.6 2.6 0 0 1 5 .9c0 1.8-2.5 2.2-2.5 3.8M12 17.2h.01"/></svg>
        </span>
        <b>No questions yet</b>
        <p>{{ isAdminOrSpeaker
          ? 'Questions appear here as they come in, most upvoted first.'
          : 'Ask anything about the topic. Others can upvote it, and the host answers here or out loud.' }}</p>
      </div>

      <template v-else>
        <div class="qp-seg" role="group" aria-label="Questions">
          <button type="button" :class="{ on: phoneTab === 'open' }" :aria-pressed="String(phoneTab === 'open')" data-testid="qa-tab-open" @click="phoneTab = 'open'">Open<i>{{ phoneOpen.length }}</i></button>
          <button type="button" :class="{ on: phoneTab === 'done' }" :aria-pressed="String(phoneTab === 'done')" data-testid="qa-tab-done" @click="phoneTab = 'done'">Answered<i>{{ phoneDone.length }}</i></button>
        </div>

        <p v-if="!phoneList.length" class="qp-note" data-testid="qa-tab-empty">
          {{ phoneTab === 'open' ? 'Nothing waiting. Every question has been answered.' : 'No answered questions yet.' }}
        </p>

        <div v-for="q in phoneList" :key="q._id" class="qp-card" :data-testid="`qa-card-${q._id}`">
          <div class="qp-top">
            <span v-if="q.isAnonymous" class="qp-av qp-av--anon" aria-hidden="true">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="10" width="16" height="11" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/></svg>
            </span>
            <span v-else class="qp-av" aria-hidden="true">
              <img v-if="q.userAvatar" :src="q.userAvatar" alt="">
              <template v-else>{{ getInitials(q.userName || 'User') }}</template>
            </span>
            <b data-testid="qa-who">{{ phoneWho(q) }}</b>
            <span v-if="isMe(q.userId)" data-testid="qa-mine">· your question</span>
          </div>
          <p class="qp-q">{{ decodeText(q.questionText) }}</p>
          <div v-if="phoneStatus(q).tone === 'ok' && writtenAnswer(q)" class="qp-ans" data-testid="qa-answer">
            <b>{{ writtenAnswer(q).who }} answered</b>{{ decodeText(writtenAnswer(q).text) }}
          </div>
          <div class="qp-foot">
            <span class="qp-st" :class="phoneStatus(q).tone && `qp-st--${phoneStatus(q).tone}`" data-testid="qa-status">
              <svg v-if="phoneStatus(q).tone === 'ok'" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5.8 12.3 4.2 4.2L18.2 7.8"/></svg>
              {{ phoneStatus(q).label }}
            </span>
            <span class="sp"></span>
            <template v-if="isAdminOrSpeaker && q.status === 'pending' && !q.local">
              <button type="button" class="qp-btn" data-testid="qa-live" @click="markAnsweredLive(q)">Answered live</button>
              <button type="button" class="qp-ib" aria-label="Dismiss" title="Dismiss" data-testid="qa-dismiss" @click="dismissQuestion(q._id)">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>
              </button>
            </template>
            <button
                v-if="canVoteHere(q)"
                type="button"
                class="qp-vote"
                :class="{ on: upvotedByMe(q) }"
                :aria-pressed="String(upvotedByMe(q))"
                aria-label="Upvote"
                data-testid="qa-vote"
                @click="upvote(q._id)"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 15 6-6 6 6"/></svg>
              {{ q.upvotes?.length || 0 }}
            </button>
            <span v-else class="qp-st qp-st--no" data-testid="qa-votes">{{ q.upvotes?.length || 0 }} votes</span>
          </div>
        </div>
      </template>
    </div>

    <!-- The participant's ask box; the host asks nothing. -->
    <form v-if="!isAdminOrSpeaker" class="qp-ask" data-testid="qa-ask" @submit.prevent="askFromPhone">
      <template v-if="settings.takingQuestions">
        <div class="qp-composer">
          <input v-model="phoneDraft" placeholder="Ask a question" aria-label="Ask a question" autocomplete="off" data-testid="qa-ask-input">
          <button type="submit" class="qp-send" aria-label="Submit question" :disabled="!phoneDraft.trim()" data-testid="qa-ask-send">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m21 3-9 18-2.5-7.5L2 11z"/></svg>
          </button>
        </div>
        <div v-if="settings.allowAnonymous" class="qp-anon">
          <XlSwitch v-model="phoneAnon" size="sm" label="Ask without my name" :label-first="false" data-testid="qa-anon" />
        </div>
      </template>
      <p v-else class="qp-closed" data-testid="qa-closed">The host isn't taking questions right now.</p>
    </form>
  </div>

  <div v-else class="qna-widget">
    <!-- Header (admin / speaker) -->
    <div v-if="isAdminOrSpeaker" class="qahead">
      <Tooltip
          v-if="tooltip.visible"
          :rect="tooltip.target?.getBoundingClientRect()"
          :target-left="tooltip.targetLeft"
          :custom="true"
          initial="bottom"
          x-position="left"
          @close="handleTooltipClose"
      >
        <div class="qna-tooltip-content">
          <div class="qna-tooltip-row">
            <IconQandA size="16" color="#ffffff"/>
            <span class="status-text">Q&A is now {{ settings.takingQuestions ? 'open' : 'closed' }}</span>
          </div>
          <div class="qna-tooltip-sub">Ask a question</div>
        </div>
      </Tooltip>

      <!-- Taking Questions Toggle -->
      <div v-if="settingsLoaded"
           ref="toggleContainerRef"
           class="toggle-container"
           @mouseenter="handleTooltipMouseEnter"
           @mouseleave="handleTooltipMouseLeave"
      >
        <XlSwitch
            class="qahead-switch"
            :model-value="settings.takingQuestions"
            label="Taking Questions"
            :label-first="false"
            @change="toggleSetting('takingQuestions')"
        />
      </div>
      <div v-else class="toggle-container loading">
        <div class="shimmer toggle-shimmer"></div>
      </div>

      <span class="sp"></span>

      <!-- Settings Menu (Admin Only) -->
      <button
          ref="settingsBtn"
          type="button"
          class="ib"
          :class="{ on: showSettings }"
          title="Settings"
          aria-label="Settings"
          :aria-expanded="showSettings ? 'true' : 'false'"
          @click="toggleSettingsMenu"
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M4.4 7.4h11M18.6 7.4h1M4.4 16.6h1M8.6 16.6h11"/>
          <circle cx="17" cy="7.4" r="2.4"/>
          <circle cx="7" cy="16.6" r="2.4"/>
        </svg>
      </button>
    </div>

    <!-- Settings panel -->
    <div v-if="isAdminOrSpeaker && showSettings" class="boset" ref="settingsDropdown">
      <div class="boset-top">
        <h4>Q&A Settings</h4>
        <button type="button" class="boset-close" aria-label="Close settings" @click="showSettings = false">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="m7.1 7.1 9.8 9.8M16.9 7.1l-9.8 9.8"/>
          </svg>
        </button>
      </div>
      <div class="boset-label">Allow members to view</div>
      <div class="row">
        <XlSwitch
            class="row-switch"
            size="sm"
            :model-value="settings.showAllQuestions"
            label="all questions"
            @change="toggleSetting('showAllQuestions')"
        />
      </div>
      <div class="qasub">
        <div class="row">
          <XlSwitch
              class="row-switch"
              size="sm"
              :model-value="settings.allowUpvotes"
              label="members can upvote"
              @change="toggleSetting('allowUpvotes')"
          />
        </div>
        <div class="row">
          <XlSwitch
              class="row-switch"
              size="sm"
              :model-value="settings.allowComments"
              label="members can comment"
              @change="toggleSetting('allowComments')"
          />
        </div>
      </div>
      <div class="row">
        <XlSwitch
            class="row-switch"
            size="sm"
            :model-value="settings.allowAnonymous"
            label="allow anonymous questions"
            @change="toggleSetting('allowAnonymous')"
        />
      </div>
    </div>

    <!-- Open / Answered / Dismissed -->
    <div class="rmsub">
      <div class="seg" role="group" aria-label="Questions">
        <button
            v-for="tab in tabsData"
            :key="tab.id"
            type="button"
            :class="{ on: activeTab === tab.id }"
            :aria-pressed="activeTab === tab.id ? 'true' : 'false'"
            @click="activeTab = tab.id"
        >{{ tab.label }}</button>
      </div>
    </div>

    <div class="qna-body">
      <div v-if="!loaded" class="widget-loader">
        <div class="shimmer header-shimmer"></div>
        <div class="shimmer tabs-shimmer"></div>
        <div class="shimmer card-shimmer"></div>
        <div class="shimmer card-shimmer"></div>
      </div>

      <!-- Content -->
      <div v-else class="questions-list">
        <div v-if="questions.length === 0 && activeTab === 'Open'" class="rmempty">
          <span class="g">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M9.4 11.6V6.2a1.5 1.5 0 0 1 3 0v5.4M12.4 11.6V5.2a1.5 1.5 0 0 1 3 0v6.4M15.4 11.6V7.2a1.5 1.5 0 0 1 3 0v7.3a5.5 5.5 0 0 1-5.5 5.5h-1a5.5 5.5 0 0 1-5.5-5.5v-2.4a1.5 1.5 0 0 1 3 0"/>
            </svg>
          </span>
          <b>Start Q&A</b>
          <p>
            To ask a question, click the “{{ questions.length === 0 ? 'Start Q&A' : 'Ask Question' }}” button below.
          </p>
        </div>

        <p v-else-if="visibleQuestions.length === 0" class="rmsubempty">
          No {{ activeTab.toLowerCase() }} questions.
        </p>

        <!-- List -->
        <div v-else class="qlist">
          <div
              v-for="qna in visibleQuestions"
              :key="qna._id"
              class="qcard"
              :class="{ dim: qna.status === 'dismissed' }"
          >
            <div class="qtop">
              <span v-if="qna.isAnonymous" class="qav anon">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <circle cx="9" cy="8" r="3.2"/><path d="M3.6 20v-1.4A4 4 0 0 1 7.6 14.6h2.8a4 4 0 0 1 4 4V20"/><circle cx="17.4" cy="9" r="2.4"/><path d="M16 14.7h1.8a3 3 0 0 1 3 3V20"/>
                </svg>
              </span>
              <span v-else class="face qav-face">
                <img v-if="qna.userAvatar" :src="qna.userAvatar" alt="Avatar"/>
                <span v-else>{{ getInitials(qna.userName || 'User') }}</span>
              </span>
              <div class="qmeta">
                <span class="qwho">{{ qna.isAnonymous ? "Anonymous" : getAuthorName(qna) }}</span>
                <span class="qtime">{{ formatTime(qna.createdAt) }}</span>
              </div>
              <span v-if="qna.status === 'pending' && !settings.showAllQuestions" class="tag wa">pending</span>
              <span v-if="qna.status === 'answered'" class="tag ok">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="m5.8 12.3 4.2 4.2L18.2 7.8"/>
                </svg>
                Approved
              </span>
              <span class="sp"></span>
              <button
                  v-if="(isAdminOrSpeaker || settings.allowUpvotes) && qna.status === 'pending' && settings.showAllQuestions"
                  type="button"
                  :class="['qvote', { 'on': upvotedByMe(qna) }]"
                  :aria-pressed="upvotedByMe(qna) ? 'true' : 'false'"
                  @click="upvote(qna._id)"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M7 10v11H4a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1h3Z"/>
                  <path d="M7 10l4.2-7a2.2 2.2 0 0 1 3.1 2.6L13 10h5.3a2 2 0 0 1 2 2.5l-1.7 7A2 2 0 0 1 16.6 21H7"/>
                </svg>
                {{ qna.upvotes.length }}
              </button>
              <div class="menu-dots" v-if="isAdminOrSpeaker">
                <button
                    type="button"
                    class="ib qmenu-btn"
                    aria-label="Question actions"
                    :aria-expanded="isMenuOpen(qna._id) ? 'true' : 'false'"
                    @click.stop="toggleMenu(qna._id)"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <circle cx="12" cy="5.5" r="1.6"/><circle cx="12" cy="12" r="1.6"/><circle cx="12" cy="18.5" r="1.6"/>
                  </svg>
                </button>
                <div v-if="isMenuOpen(qna._id)" class="qmenu">
                  <button v-if="qna.status !== 'dismissed'" type="button" class="qmenu-item" @click="dismissQuestion(qna._id)">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <path d="m7.1 7.1 9.8 9.8M16.9 7.1l-9.8 9.8"/>
                    </svg>
                    Dismiss
                  </button>
                  <button type="button" class="qmenu-item cr" @click="deleteQuestion(qna._id)">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <path d="M4.8 7h14.4"/><path d="M9.6 7V5.2a1 1 0 0 1 1-1h2.8a1 1 0 0 1 1 1V7"/><path d="M6.6 7l.9 11.4a1.6 1.6 0 0 0 1.6 1.4h5.8a1.6 1.6 0 0 0 1.6-1.4L17.4 7"/><path d="M10.2 10.8v5.4M13.8 10.8v5.4"/>
                    </svg>
                    Delete
                  </button>
                </div>
              </div>
            </div>

            <p class="qq">{{ qna.questionText }}</p>

            <button
                v-if="canViewComments(qna)"
                type="button"
                class="qcomments-toggle"
                :aria-expanded="isCommentsOpen(qna._id) ? 'true' : 'false'"
                @click="toggleComments(qna._id)"
            >
              <span>Comments</span>
              <span v-if="qna.comments.length > 0">
                ({{ qna.comments.length }})
              </span>
              <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.1"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="chevron-icon"
                  :class="{ rotated: isCommentsOpen(qna._id) }"
                  aria-hidden="true"
              >
                <path d="m6 9.8 6 6 6-6"/>
              </svg>
            </button>

            <!-- Comments Section -->
            <div class="qcomments" v-if="canViewComments(qna) && isCommentsOpen(qna._id)">
              <div v-if="qna.status === 'answered' && qna.isAnsweredLive" class="qans">
                <div class="qans-head">
                  <span class="face qans-face">
                    <img v-if="qna.answeredByAvatar" :src="qna.answeredByAvatar" alt="Avatar"/>
                    <span v-else>{{ getInitials(qna.answeredBy || 'Admin') }}</span>
                  </span>
                  <b>{{ qna.answeredById === userId ? 'You' : (qna.answeredBy || 'Admin') }}</b>
                  <span class="qans-time">{{ formatTime(qna.answeredAt || qna.updatedAt) }}</span>
                </div>
                <p class="live-answer">This question has been answered live.</p>
              </div>

              <div v-for="comment in qna.comments" :key="comment.createdAt" class="qans">
                <div class="qans-head">
                  <span class="face qans-face">
                    <img v-if="comment.userAvatar" :src="comment.userAvatar" alt="Avatar"/>
                    <span v-else>{{ getInitials(comment.userName || 'Admin') }}</span>
                  </span>
                  <b>{{ comment.userId === userId ? 'You' : (comment.userName || 'Admin') }}</b>
                  <span class="qans-time">{{ formatTime(comment.createdAt) }}</span>
                </div>
                <p>{{ comment.text }}</p>
              </div>

              <!-- Comment Input -->
              <div v-if="isAdminOrSpeaker || settings.allowComments" class="qform-wrap">
                <div class="qform-head">
                  <span class="face qans-face">
                    <img v-if="currentUserAvatar" :src="currentUserAvatar" alt="User"/>
                    <span v-else>{{ currentUserInitials }}</span>
                  </span>
                  <b>You</b>
                  <span class="qans-time">{{ formatTime(new Date()) }}</span>
                </div>
                <div class="qform">
                  <input
                      type="text"
                      class="inp"
                      placeholder="Add a comment..."
                      aria-label="Add a comment"
                      v-model="commentInputs[qna._id]"
                      @keyup.enter="submitComment(qna._id)"
                  />
                  <button
                      type="button"
                      class="rnd p"
                      aria-label="Send comment"
                      @click="submitComment(qna._id)"
                      :disabled="!commentInputs[qna._id]?.trim()"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <path d="M19.4 4.6 5 10.4l5.8 2.6 2.6 5.8z"/><path d="M19.4 4.6 10.8 13"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            <!-- Admin Actions -->
            <div v-if="isAdminOrSpeaker && qna.status === 'pending'" class="qact">
              <XlButton variant="ghost" size="sm" @click="openAnsweringModal(qna._id, 'live')">Answer Live</XlButton>
              <XlButton size="sm" @click="openAnsweringModal(qna._id, 'text')">Answer by Text</XlButton>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="qcomp" v-if="settings.takingQuestions && activeTab !== 'Dismissed'">
      <button type="button" class="qask" @click="openAskModal">
        <span class="qask-label">{{ questions.length === 0 ? 'Start Q&A' : 'Ask Question' }}</span>
        <span class="rnd p" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
            <path d="M19.4 4.6 5 10.4l5.8 2.6 2.6 5.8z"/><path d="M19.4 4.6 10.8 13"/>
          </svg>
        </span>
      </button>
    </div>
  </div>

  <!-- Ask Modal -->
  <div v-if="showAskModal" class="mod">
    <div class="mdl ses" role="dialog" aria-modal="true" aria-labelledby="qna-ask-title">
      <div class="mh">
        <div class="sesmark">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M9.4 11.6V6.2a1.5 1.5 0 0 1 3 0v5.4M12.4 11.6V5.2a1.5 1.5 0 0 1 3 0v6.4M15.4 11.6V7.2a1.5 1.5 0 0 1 3 0v7.3a5.5 5.5 0 0 1-5.5 5.5h-1a5.5 5.5 0 0 1-5.5-5.5v-2.4a1.5 1.5 0 0 1 3 0"/>
          </svg>
        </div>
        <h3 id="qna-ask-title">Ask Question</h3>
      </div>
      <div class="mb">
        <textarea
            v-model="newQuestionText"
            class="inp area"
            placeholder="Type your question..."
            aria-label="Your question"
            rows="4"
            :maxlength="350"
        ></textarea>

        <div class="mod-meta">
          <div class="char-count">{{ newQuestionText.length }} / 350</div>
          <div v-if="settings.allowAnonymous" class="qanon">
            <XlSwitch
                class="qanon-switch"
                size="sm"
                v-model="askAnonymous"
                label="Hide my name"
                :label-first="false"
            />
          </div>
          <div v-else class="privacy-note">Your name will be visible</div>
        </div>
        <div v-if="settings.allowAnonymous" class="privacy-status">
          {{ askAnonymous ? 'Anonymous' : 'Your name will be visible' }}
        </div>
      </div>

      <div class="mf">
        <span class="sp"></span>
        <XlButton variant="ghost" @click="showAskModal = false">Cancel</XlButton>
        <XlButton :disabled="!newQuestionText.trim()" @click="submitQuestion">Submit</XlButton>
      </div>
    </div>
  </div>

  <!-- Answering Modal (Live or Text) -->
  <div v-if="showAnsweringModal && selectedQuestionForLive" class="mod">
    <div class="mdl ses" role="dialog" aria-modal="true" aria-labelledby="qna-answer-title">
      <div class="mh">
        <div class="sesmark">
          <svg v-if="answeringMode === 'live'" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <rect x="9.2" y="4.2" width="5.6" height="9.8" rx="2.8"/><path d="M6 11.4a6 6 0 0 0 12 0"/><path d="M12 17.4V19.8"/>
          </svg>
          <svg v-else width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M19.4 4.6 5 10.4l5.8 2.6 2.6 5.8z"/><path d="M19.4 4.6 10.8 13"/>
          </svg>
        </div>
        <h3 id="qna-answer-title">Answering</h3>
      </div>

      <div class="mb">
        <!-- Simplified Question Preview -->
        <div class="qcard preview">
          <div class="qtop">
            <span v-if="selectedQuestionForLive.isAnonymous" class="qav anon">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <circle cx="9" cy="8" r="3.2"/><path d="M3.6 20v-1.4A4 4 0 0 1 7.6 14.6h2.8a4 4 0 0 1 4 4V20"/><circle cx="17.4" cy="9" r="2.4"/><path d="M16 14.7h1.8a3 3 0 0 1 3 3V20"/>
              </svg>
            </span>
            <span v-else class="face qav-face">
              <img v-if="selectedQuestionForLive.userAvatar" :src="selectedQuestionForLive.userAvatar" alt="Avatar"/>
              <span v-else>{{ getInitials(selectedQuestionForLive.userName || 'User') }}</span>
            </span>
            <div class="qmeta">
              <span class="qwho">{{ selectedQuestionForLive.isAnonymous ? "Anonymous" : getAuthorName(selectedQuestionForLive) }}</span>
              <span class="qtime">{{ formatTime(selectedQuestionForLive.createdAt) }}</span>
            </div>
            <span class="sp"></span>
            <span class="qvotes">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M7 10v11H4a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1h3Z"/>
                <path d="M7 10l4.2-7a2.2 2.2 0 0 1 3.1 2.6L13 10h5.3a2 2 0 0 1 2 2.5l-1.7 7A2 2 0 0 1 16.6 21H7"/>
              </svg>
              {{ selectedQuestionForLive.upvotes.length }}
            </span>
          </div>
          <p class="qq">{{ selectedQuestionForLive.questionText }}</p>
        </div>

        <p v-if="answeringMode === 'live'" class="sesfacts live-answer-note">
          You would like to answer this question live.
        </p>

        <div v-if="answeringMode === 'text'" class="text-answer-area">
          <textarea
              v-model="answerTextModal"
              class="inp area"
              placeholder="Type your answer..."
              aria-label="Your answer"
              rows="4"
              :maxlength="350"
          ></textarea>
          <div class="mod-meta">
            <div class="char-count">{{ answerTextModal.length }} / 350</div>
            <div class="privacy-note">Your name will be visible</div>
          </div>
        </div>
      </div>

      <div class="mf">
        <span class="sp"></span>
        <XlButton variant="ghost" @click="closeAnsweringModal">Cancel</XlButton>
        <XlButton @click="confirmAnswering">{{ answeringMode === 'live' ? 'Done' : 'Submit' }}</XlButton>
      </div>
    </div>
  </div>
  <SessionDialog v-if="modalRemoveQnA" title="Delete Question" tone="cr" icon="trash" dismissible data-testid="qna-delete-dialog" @close="modalRemoveQnA = null">
    <p>Are you sure you want to delete this question?</p>
    <template #actions>
      <XlButton variant="ghost" @click="modalRemoveQnA = null">Cancel</XlButton>
      <XlButton variant="danger" @click="confirmDeleteQnA">Delete</XlButton>
    </template>
  </SessionDialog>
</template>

<script setup>
import {ref, computed, reactive, onMounted, onUnmounted, watch} from "vue";
import {useSocketStore} from "../../stores/socketStore";
import {useAuthStore} from "../../stores/auth";
import {useSessionStore} from "../../stores/sessionStore";
import IconQandA from "../icons/IconQandA.vue";
import SessionDialog from "../../livekit-client/components/SessionDialog.vue";
import XlButton from "../xl/XlButton.vue";
import XlSwitch from "../xl/XlSwitch.vue";
import Tooltip from "../chat/shared/tooltip/Tooltip.vue";
import { timeOfDay } from '../../helpers/usDate';


const props = defineProps({
  sessionId: String,
  userId: String,
  userRole: String,
  userName: String,
  domain: String,
  isActive: {
    type: Boolean,
    default: false
  }
});

// The service records ids from the verified chat token, as numbers; the page
// knows the viewer's as a string. Compared as strings, or a viewer's own
// upvote, question and "Mine" filter never matched them (as Polls' votes).
const isMe = (id) => id != null && props.userId != null && String(id) === String(props.userId);
const upvotedByMe = (qna) => (qna.upvotes ?? []).some(isMe);

const socketStore = useSocketStore();
const authStore = useAuthStore();

// State
const questions = ref([]);
const activeTab = ref("Open");
// const tabs = ["Open", "Answered", "Dismissed"]; // Removed in favor of tabsData
const showSettings = ref(false);
const showAskModal = ref(false);
const showAnsweringModal = ref(false);
const selectedQuestionForLive = ref(null);
const answeringMode = ref('live'); // 'live' or 'text'
const answerTextModal = ref("");
const newQuestionText = ref("");
const askAnonymous = ref(false);
const loaded = ref(false);
const modalRemoveQnA = ref(null);
const unreadCount = ref(0);
const settingsDropdown = ref(null);
const settingsBtn = ref(null);
const toggleContainerRef = ref(null);

const tooltip = ref({
  visible: false,
  target: null,
  text: "",
  initial: "bottom",
  xPosition: "center",
  targetLeft: false
});

const settings = reactive({
  takingQuestions: false, // Default to false to prevent flicker
  showAllQuestions: true,
  allowUpvotes: true,
  allowComments: true,
  allowAnonymous: true,
});
const settingsLoaded = ref(false);

const openComments = ref(new Set());
const openMenus = ref(new Set());
const answeringId = ref(null);
const answerText = ref("");
const commentInputs = ref({});

// Computed
const isAdminOrSpeaker = computed(() => {
  const role = props.userRole?.toLowerCase();
  const allowedRoles = ['administrator', 'speaker', 'admin', 'event-planner', 'owner'];

  // Check prop role
  if (allowedRoles.includes(role)) return true;

  // Fallback to authStore role
  const storeRole = useAuthStore().userRole?.toLowerCase();
  if (allowedRoles.includes(storeRole)) return true;

  return false;
});

const answeredCount = computed(() =>
    questions.value.filter(q => q.status === 'answered').length
);

const currentUserInitials = computed(() => {
  const name = authStore.userData?.fullName || props.userName || "Guest";
  return getInitials(name);
});

const currentUserAvatar = computed(() => {
  return authStore.userData?.profile_image || null;
});

const visibleQuestions = computed(() => {
  let filtered = questions.value;

  // Filter by Tab
  if (activeTab.value === "Open") {
    filtered = filtered.filter((q) => q.status === 'pending');
  } else if (activeTab.value === "Answered") {
    filtered = filtered.filter((q) => q.status === 'answered');
  } else if (activeTab.value === "Dismissed") {
    filtered = filtered.filter((q) => q.status === 'dismissed');
  }

  // Filter by Visibility Settings (for Attendees)
  if (!isAdminOrSpeaker.value) {
    if (!settings.showAllQuestions) {
      // Attendees only see their own questions, regardless of status
      filtered = filtered.filter(q => isMe(q.userId));
    }
    // If Dismissed tab, usually attendees don't see dismissed?
    if (activeTab.value === "Dismissed") {
      return []; // Hide dismissed from attendees
    }
  }

  // Sort: Most liked first if enabled and in Open tab, otherwise newest first
  return filtered.sort((a, b) => {
    if (settings.allowUpvotes && activeTab.value === 'Open') {
      const upvotesA = a.upvotes?.length || 0;
      const upvotesB = b.upvotes?.length || 0;
      if (upvotesB !== upvotesA) {
        return upvotesB - upvotesA;
      }
    }
    return new Date(b.createdAt) - new Date(a.createdAt);
  });
});

const tabsData = computed(() => {
  const allTabs = [
    {id: 'Open', label: 'Open'},
    {id: 'Answered', label: 'Answered'},
    {id: 'Dismissed', label: 'Dismissed'}
  ];
  if (isAdminOrSpeaker.value) {
    return allTabs;
  }
  return allTabs.filter(t => t.id !== 'Dismissed');
});

// Methods
const getAuthorName = (qna) => {
  if (isMe(qna.userId)) return "You";
  return qna.userName || `User ${qna.userId.slice(-4)}`;
};

const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

const formatTime = (iso) => {
  if (!iso) return "";
  return timeOfDay(iso);
};

const toggleComments = (id) => {
  if (openComments.value.has(id)) {
    openComments.value.delete(id);
  } else {
    openComments.value.add(id);
  }
};

const isCommentsOpen = (id) => openComments.value.has(id);

const toggleMenu = (id) => {
  if (openMenus.value.has(id)) {
    openMenus.value.delete(id);
  } else {
    openMenus.value = new Set([id]); // Close others
  }
};
const isMenuOpen = (id) => openMenus.value.has(id);

const canViewComments = (qna) => {
  const isPendingAdmin = isAdminOrSpeaker.value && qna.status === 'pending';
  if (isPendingAdmin) return false;

  const hasContent = qna.comments.length > 0 || (qna.status === 'answered' && qna.isAnsweredLive);
  const canComment = isAdminOrSpeaker.value || settings.allowComments;

  return hasContent || canComment;
};

// Actions
const toggleSetting = (key) => {
  if (!isAdminOrSpeaker.value) return;

  // Toggle local first (optimistic)
  settings[key] = !settings[key];

  socketStore.emit('qna:settings:update', {
    sessionId: props.sessionId,
    domain: props.domain || window.store.subdomain,
    settings: {...settings}
  });
};

const openAskModal = () => {
  newQuestionText.value = "";
  askAnonymous.value = false;
  showAskModal.value = true;
};

const submitQuestion = () => {
  if (!newQuestionText.value.trim()) return;

  socketStore.emit("qna:ask", {
    sessionId: props.sessionId,
    domain: props.domain || window.store.subdomain,
    userId: props.userId,
    userRole: props.userRole,
    userName: props.userName,
    questionText: newQuestionText.value,
    isAnonymous: settings.allowAnonymous && askAnonymous.value,
  });
  showAskModal.value = false;
};

const upvote = (qnaId) => {
  const qna = questions.value.find(q => q._id === qnaId);
  if (!qna || qna.status !== 'pending') return;

  // Enforce visibility setting for pending questions
  if (!settings.showAllQuestions) return;

  // Check if allowed
  if (!isAdminOrSpeaker.value && !settings.allowUpvotes) return;
  socketStore.emit("qna:upvote", {qnaId, userId: props.userId});
};

const openAnsweringModal = (qnaId, mode) => {
  const q = questions.value.find(x => x._id === qnaId);
  if (q) {
    selectedQuestionForLive.value = q;
    answeringMode.value = mode;
    answerTextModal.value = ""; // Reset text

    // If text mode, maybe pre-fill if editing? (not needed for now)

    showAnsweringModal.value = true;
  }
};

const closeAnsweringModal = () => {
  showAnsweringModal.value = false;
  selectedQuestionForLive.value = null;
  answerTextModal.value = "";
};

const confirmAnswering = () => {
  if (!selectedQuestionForLive.value) return;

  if (answeringMode.value === 'live') {
    socketStore.emit("qna:answer_live", {
      qnaId: selectedQuestionForLive.value._id,
      userName: props.userName,
      userId: props.userId
    });
    closeAnsweringModal();
  } else {
    // Text Answer
    if (!answerTextModal.value.trim()) return;

    socketStore.emit("qna:comment", {
      qnaId: selectedQuestionForLive.value._id,
      userId: props.userId,
      userRole: props.userRole,
      userName: props.userName, // Changed from "Admin"
      text: answerTextModal.value
    });
    closeAnsweringModal();
  }
};

const submitComment = (qnaId) => {
  const text = commentInputs.value[qnaId];
  if (!text || !text.trim()) return;

  socketStore.emit("qna:comment", {
    qnaId: qnaId,
    userId: props.userId,
    userRole: props.userRole,
    userName: props.userName,
    text: text
  });

  // Clear input
  commentInputs.value[qnaId] = "";
};

const dismissQuestion = (qnaId) => {
  openMenus.value.delete(qnaId);
  socketStore.emit("qna:update_status", {qnaId, status: 'dismissed'});
};

// ----- Q&A on a phone (Mobile in-session → Q&A; prototype sheetQA) -----
// The same questions and the same socket actions, drawn as the prototype's
// sheet: Open (open and answered live, most upvoted first) and Answered; an
// ask box at the bottom with "Ask without my name" on; for the host,
// Answered live and Dismiss on each open question. The desktop is as it was.
const phoneStore = useSessionStore();
const phoneQa = computed(() => phoneStore.phoneRoom);
const phoneTab = ref('open');
const phoneDraft = ref('');
const phoneAnon = ref(true);
// What was just asked here, shown at once until the server's copy arrives.
const pendingAsks = ref([]);
const MODERATOR_ROLES = ['administrator', 'speaker', 'admin', 'event-planner', 'owner'];

const decodeText = (text) => String(text ?? '')
  .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, '&');
const seenHere = (q) => isAdminOrSpeaker.value || settings.showAllQuestions || isMe(q.userId);
const isLive = (q) => q.status === 'answered' && q.isAnsweredLive;
const byVotes = (a, b) => ((b.upvotes?.length || 0) - (a.upvotes?.length || 0)) || (new Date(b.createdAt) - new Date(a.createdAt));

const phoneAll = computed(() => [...pendingAsks.value, ...questions.value].filter(seenHere));
const phoneOpen = computed(() => phoneAll.value.filter((q) => q.status === 'pending' || isLive(q)).sort(byVotes));
// Dismissed ones stay for the host and the person who asked; nobody else.
const phoneDone = computed(() => phoneAll.value.filter((q) => (q.status === 'answered' && !q.isAnsweredLive)
  || (q.status === 'dismissed' && (isAdminOrSpeaker.value || isMe(q.userId)))));
const phoneList = computed(() => (phoneTab.value === 'open' ? phoneOpen.value : phoneDone.value));

const phoneWho = (q) => (q.isAnonymous ? 'Anonymous' : isMe(q.userId) ? 'You' : (q.userName || 'Somebody'));
const PHONE_STATUS = {
  open: { label: 'Open', tone: '' },
  live: { label: 'Answered live', tone: 'live' },
  answered: { label: 'Answered', tone: 'ok' },
  dismissed: { label: 'Not taken by the host', tone: 'no' },
};
const phoneStatus = (q) => PHONE_STATUS[q.status === 'pending' ? 'open' : isLive(q) ? 'live' : q.status] ?? PHONE_STATUS.open;
const writtenAnswer = (q) => {
  const comment = (q.comments ?? []).find((c) => MODERATOR_ROLES.includes(String(c.userRole ?? '').toLowerCase()));
  return comment ? { who: isMe(comment.userId) ? 'You' : (comment.userName || 'The host'), text: comment.text } : null;
};
const canVoteHere = (q) => q.status === 'pending' && !q.local && settings.showAllQuestions
  && (isAdminOrSpeaker.value || settings.allowUpvotes);

const askFromPhone = () => {
  const text = phoneDraft.value.trim();
  if (!text || !settings.takingQuestions) return;
  const anonymous = settings.allowAnonymous && phoneAnon.value;
  pendingAsks.value = [{
    _id: `local-${Date.now()}`, local: true, sessionId: props.sessionId, userId: props.userId, userName: props.userName,
    questionText: text, isAnonymous: anonymous, status: 'pending', upvotes: [props.userId], comments: [],
    createdAt: new Date().toISOString(),
  }, ...pendingAsks.value];
  socketStore.emit("qna:ask", {
    sessionId: props.sessionId,
    domain: props.domain || window.store.subdomain,
    userId: props.userId,
    userRole: props.userRole,
    userName: props.userName,
    questionText: text,
    isAnonymous: anonymous,
  });
  phoneDraft.value = '';
  phoneTab.value = 'open';
};

// The server's copy of what was asked here: it replaces the one shown at
// once, and gets its author's upvote, as a question asked is one its author
// wants answered.
watch(questions, (list) => {
  if (!pendingAsks.value.length) return;
  pendingAsks.value = pendingAsks.value.filter((local) => {
    const real = list.find((q) => isMe(q.userId) && decodeText(q.questionText) === local.questionText
      && new Date(q.createdAt) >= new Date(local.createdAt) - 60_000);
    if (!real) return true;
    if (!upvotedByMe(real)) socketStore.emit("qna:upvote", { qnaId: real._id, userId: props.userId });
    return false;
  });
}, { deep: true });

const markAnsweredLive = (q) => socketStore.emit("qna:answer_live", { qnaId: q._id, userName: props.userName, userId: props.userId });

/* Click Outside Logic */
const toggleSettingsMenu = () => {
  showSettings.value = !showSettings.value;
};

const handleClickOutside = (event) => {
  if (showSettings.value &&
      settingsDropdown.value &&
      !settingsDropdown.value.contains(event.target) &&
      settingsBtn.value &&
      !settingsBtn.value.contains(event.target)) {
    showSettings.value = false;
  }

  if (openMenus.value.size > 0 && !event.target.closest('.menu-dots')) {
    openMenus.value = new Set();
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

const deleteQuestion = (qnaId) => {
  openMenus.value.delete(qnaId);
  modalRemoveQnA.value = qnaId;
};

const confirmDeleteQnA = () => {
  if (modalRemoveQnA.value) {
    socketStore.emit("qna:delete", {qnaId: modalRemoveQnA.value});
    modalRemoveQnA.value = null;
  }
};

/* Tooltip Logic */
const handleTooltipMouseEnter = async () => {
  if (toggleContainerRef.value) {
    tooltip.value.target = toggleContainerRef.value;
    tooltip.value.targetLeft = false;
    tooltip.value.visible = true;
  }
};

const handleTooltipMouseLeave = () => {
  tooltip.value.targetLeft = true;
};

const handleTooltipClose = () => {
  tooltip.value.visible = false;
  tooltip.value.target = null;
  tooltip.value.targetLeft = false;
};

// Handlers
const handleSettings = (newSettings) => {
  console.log("QnAWidget received settings:", newSettings);
  if (newSettings) {
    Object.assign(settings, newSettings);
    settingsLoaded.value = true;
  }
};

const handleNew = (q) => {
  if (q.sessionId !== props.sessionId) return;
  if (!questions.value.find(ex => ex._id === q._id)) {
    questions.value.unshift(q); // Add to top
    // New question handling for notifications
    // If I am admin/speaker: Notify on pending questions
    // If I am attendee: Notify on showAllQuestions OR if it's my question (implicit)
    if (!props.isActive) {
      const before = unreadCount.value;
      if (isAdminOrSpeaker.value) {
        if (q.status === 'pending') {
          unreadCount.value++;
        }
      } else {
        // Attendee
        // If setting showAllQuestions is ON, or it is relevant?
        // Actually, if moderation is ON, attendees don't see pending unless it's theirs.
        // If moderation is OFF, they see all.
        // Let's rely on what `visibleQuestions` would show, or just simplified logic:
        // If settings.showAllQuestions || q.userId === props.userId - wait, usually notifications are for *others'* activity.
        // If moderation is OFF, attendees see all, so notify.
        if (settings.showAllQuestions || q.status === 'answered') {
          unreadCount.value++;
        }
      }
      // A phone tells of somebody else's question while Q&A is closed.
      if (phoneQa.value && unreadCount.value > before && !isMe(q.userId)) {
        phoneStore.setQuestionNotice({ text: decodeText(q.questionText), at: Date.now() });
      }
    }
  }
};

const handleUpdated = (q) => {
  if (q.sessionId !== props.sessionId) return;
  const idx = questions.value.findIndex(ex => ex._id === q._id);

  if (idx !== -1) {
    // Check for status change to 'answered' which warrants notification for attendee
    if (!props.isActive && !isAdminOrSpeaker.value && q.status === 'answered' && questions.value[idx].status !== 'answered') {
      unreadCount.value++;
    }
    questions.value[idx] = q;
  } else {
    questions.value.unshift(q);
    if (!props.isActive && !isAdminOrSpeaker.value && q.status === 'answered') {
      unreadCount.value++;
    }
  }
};

const handleAll = (all) => {
  questions.value = all;
  loaded.value = true;

  // Initial Read/Unread Sync
  if (!props.isActive) {
    if (isAdminOrSpeaker.value) {
      unreadCount.value = questions.value.filter(q => q.status === 'pending').length;
    } else {
      // For attendees, maybe unread = answered count? Or just 0 if we assume history is read?
      // Let's assume history is "new" to the user session.
      // It matches the previous logic of "status count" but now it clears.
      unreadCount.value = questions.value.filter(q => q.status === 'answered').length;
    }
  }
};

const handleDeleted = ({qnaId}) => {
  questions.value = questions.value.filter(q => q._id !== qnaId);
};

onMounted(() => {
  socketStore.on("qna:new", handleNew);
  socketStore.on("qna:updated", handleUpdated);
  socketStore.on("qna:all", handleAll);
  socketStore.on("qna:deleted", handleDeleted);
  socketStore.on("qna:settings", handleSettings);

  // Ensure we have user data for initials
  authStore.retrieveUserData();

  // Fetch initial data
  fetchAllData();
});

watch(() => props.isActive, (active) => {
  if (active) {
    unreadCount.value = 0;
  }
}, {immediate: true});

const emit = defineEmits(['update-notifications']);

watch(unreadCount, (newVal) => {
  emit('update-notifications', newVal);
}, {immediate: true});

// What was asked, for the host's summary when the session ends (on a phone).
const qaStore = useSessionStore();
watch(questions, (list) => {
  const kept = list.filter((q) => q.status !== 'dismissed');
  qaStore.setQaSummary({
    asked: kept.length,
    answered: kept.filter((q) => q.status === 'answered').length,
    open: kept.filter((q) => q.status === 'pending').length,
    // What the phone's Open tab holds for this viewer, for the sheet's count.
    openList: phoneOpen.value.length,
  });
}, {deep: true, immediate: true});
watch(() => phoneOpen.value.length, (n) => qaStore.setQaSummary({ ...qaStore.qaSummary, openList: n }));

onUnmounted(() => {
  socketStore.off("qna:new", handleNew);
  socketStore.off("qna:updated", handleUpdated);
  socketStore.off("qna:all", handleAll);
  socketStore.off("qna:deleted", handleDeleted);
  socketStore.off("qna:settings", handleSettings);
});


const fetchAllData = () => {
  if (!props.sessionId) {
    console.warn("QnAWidget: No sessionId provided, skipping fetch");
    return;
  }
  console.log("QnAWidget: Fetching all data for session", props.sessionId);
  socketStore.emit("qna:get_all", {
    sessionId: props.sessionId,
    domain: props.domain || window.store.subdomain,
    userRole: props.userRole
  });
  socketStore.emit("qna:settings:get", {
    sessionId: props.sessionId,
    domain: props.domain || window.store.subdomain
  });
};

watch(() => props.sessionId, (newVal) => {
  if (newVal) {
    fetchAllData();
  }
});

</script>

<style scoped lang="scss">
/* Prototype tokens, read from the northstar design tokens with the prototype
   hex as the fallback. Declared on both roots because the ask/answer overlays
   are siblings of .qna-widget, not children. */
.qna-widget,
.mod {
  --qa-pur: var(--xl-color-primary, #5E2F78);
  --qa-pur-2: var(--xl-color-surface-tint, #8154A6);
  --qa-tint: var(--xl-color-primary-container, #F5EFF9);
  --qa-line: var(--xl-color-border, #EDE7F0);
  --qa-line-2: var(--xl-color-border-subtle, #F6F2F8);
  --qa-ink: var(--xl-color-on-surface, #1C1720);
  --qa-ink-2: var(--xl-color-on-surface-variant, #514A57);
  --qa-mut: var(--xl-color-outline, #867D8D);
  --qa-faint: var(--xl-color-outline-variant, #B6AEBC);
  --qa-paper: var(--xl-color-surface, #FCFAFD);
  --qa-cr: var(--xl-color-error, #A32020);
  --qa-cr-bg: var(--xl-color-error-container, #FCEBEB);
  --qa-ok: var(--xl-color-success, #166B48);
  --qa-ok-bg: var(--xl-color-success-container, #E4F3EB);
  --qa-wa: var(--xl-color-warning, #8A5200);
  --qa-wa-bg: var(--xl-color-warning-container, #FDF2E2);
  --qa-sh: 0 1px 2px rgba(28, 23, 32, .04);
  font-family: var(--xl-font-family, 'General Sans', Inter, -apple-system, 'Segoe UI', sans-serif);
  letter-spacing: -.006em;
  color: var(--qa-ink);
}

.qna-widget *,
.mod * {
  box-sizing: border-box;
}

:where(.qna-widget, .mod) button {
  font: inherit;
  color: inherit;
  cursor: pointer;
  border: 0;
  background: none;
  padding: 0;
}

.sp {
  flex: 1;
}

.qna-widget {
  width: 100%;
  height: 100%;
  background: #fff;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  min-height: 0;
}

/* ---------- Admin header (spec 3.1) ---------- */
.qahead {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 14px 10px;
  border-bottom: 1px solid var(--qa-line-2);
  flex-shrink: 0;
}

.toggle-container {
  display: flex;
  align-items: center;
  min-width: 0;
}

.toggle-container.loading .toggle-shimmer {
  width: 150px;
  height: 20px;
}

.toggle-container .qahead-switch {
  gap: 10px;
}

.toggle-container .qahead-switch :deep(.xl-switch__text b) {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--qa-ink);
}

.ib {
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid var(--qa-line);
  cursor: pointer;
  transition: background .13s, color .13s;
}

.ib:hover {
  background: var(--qa-paper);
}

.ib:focus-visible,
.qvote:focus-visible,
.qask:focus-visible,
.rnd:focus-visible,
.rmsub .seg button:focus-visible,
.qcomments-toggle:focus-visible,
.qmenu-item:focus-visible,
.boset-close:focus-visible {
  outline: 2px solid var(--qa-pur);
  outline-offset: 2px;
}

.qahead .ib {
  width: 28px;
  height: 28px;
  flex: 0 0 28px;
  border-radius: 8px;
  color: var(--qa-mut);
}

.qahead .ib.on {
  background: var(--qa-tint);
  color: var(--qa-pur);
}

/* ---------- Settings (spec 3.1 .boset) ---------- */
.boset {
  flex-shrink: 0;
  margin: 0 14px 12px;
  padding: 2px 14px;
  background: var(--qa-paper);
  border: 1px solid var(--qa-line);
  border-radius: 14px;
}

.boset-top {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 0 2px;
}

.boset-top h4 {
  flex: 1;
  margin: 0;
  font-size: 13.5px;
  font-weight: 600;
  color: var(--qa-ink);
}

.boset-close {
  width: 24px;
  height: 24px;
  border-radius: 7px;
  display: grid;
  place-items: center;
  color: var(--qa-mut);
}

.boset-close:hover {
  background: var(--qa-line-2);
  color: var(--qa-ink);
}

.boset-label {
  padding: 8px 0 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .08em;
  text-transform: uppercase;
  color: var(--qa-faint);
}

.boset .row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 0;
  border-bottom: 1px solid var(--qa-line-2);
}

.boset > .row:last-child {
  border-bottom: none;
}

.qasub {
  margin-left: 14px;
  padding-left: 10px;
  border-left: 2px solid var(--qa-line-2);
}

.boset .row-switch {
  width: 100%;
  justify-content: space-between;
  gap: 14px;
}

.boset .row-switch :deep(.xl-switch__text b) {
  font-size: 13px;
  font-weight: 600;
  color: var(--qa-ink);
}

/* .sw.sm: 30 x 18, 12px knob */
.boset .row-switch :deep(.xl-switch__track),
.qanon .qanon-switch :deep(.xl-switch__track) {
  width: 30px;
  height: 18px;
  flex: 0 0 30px;
}

.boset .row-switch :deep(.xl-switch__track i),
.qanon .qanon-switch :deep(.xl-switch__track i) {
  width: 12px;
  height: 12px;
  top: 3px;
  left: 3px;
}

.boset .row-switch :deep(.xl-switch__input:checked + .xl-switch__track i),
.qanon .qanon-switch :deep(.xl-switch__input:checked + .xl-switch__track i) {
  left: 15px;
}

/* ---------- Open / Answered / Dismissed (panel .rmsub .seg) ---------- */
.rmsub {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px 2px;
  background: #fff;
}

.rmsub .seg {
  flex: 1;
  display: flex;
  gap: 2px;
  padding: 3px;
  background: var(--qa-line-2);
  border-radius: 10px;
}

.rmsub .seg button {
  flex: 1;
  height: 26px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 600;
  color: var(--qa-mut);
  border: none;
  background: none;
  transition: background .13s, color .13s;
}

.rmsub .seg button.on {
  background: #fff;
  color: var(--qa-pur);
  box-shadow: var(--qa-sh);
}

/* ---------- Body ---------- */
.qna-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.questions-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.rmempty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 30px 24px;
  gap: 4px;
}

.rmempty .g {
  width: 46px;
  height: 46px;
  border-radius: 14px;
  background: var(--qa-line-2);
  color: var(--qa-mut);
  display: grid;
  place-items: center;
  margin-bottom: 8px;
}

.rmempty b {
  font-size: 14.5px;
  color: var(--qa-ink);
}

.rmempty p {
  font-size: 12.5px;
  color: var(--qa-mut);
  margin: 0 0 4px;
  line-height: 1.55;
  max-width: 230px;
}

.rmsubempty {
  margin: 0;
  padding: 26px 20px;
  text-align: center;
  font-size: 13px;
  color: var(--qa-mut);
}

/* ---------- Question card (spec 3.3) ---------- */
.qlist {
  padding: 10px 14px 2px;
}

.qcard {
  border: 1px solid var(--qa-line);
  border-radius: 14px;
  background: #fff;
  padding: 14px;
  margin-bottom: 10px;
  box-shadow: 0 1px 2px rgba(28, 23, 32, .03);
}

.qcard.dim {
  background: var(--qa-paper);
  border-style: dashed;
}

.qcard.dim .qq {
  color: var(--qa-mut);
}

.qtop {
  display: flex;
  align-items: center;
  gap: 9px;
  margin-bottom: 9px;
  min-width: 0;
}

.qav.anon {
  width: 28px;
  height: 28px;
  flex: 0 0 28px;
  border-radius: 50%;
  background: var(--qa-line-2);
  color: var(--qa-mut);
  display: grid;
  place-items: center;
}

.face {
  border-radius: 50%;
  overflow: hidden;
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  color: #fff;
  font-weight: 600;
  background: linear-gradient(150deg, var(--qa-pur-2), var(--qa-pur));
}

.face img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.qav-face {
  width: 28px;
  height: 28px;
  font-size: 10px;
}

.qmeta {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.25;
}

.qwho {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--qa-ink-2);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.qtime {
  font-size: 11px;
  color: var(--qa-faint);
  font-variant-numeric: tabular-nums;
}

.tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 23px;
  padding: 0 9px;
  border-radius: 7px;
  font-size: 11.5px;
  font-weight: 600;
  background: var(--qa-line-2);
  color: var(--qa-ink-2);
  white-space: nowrap;
  flex: 0 0 auto;
  line-height: 1;
}

.tag.ok {
  background: var(--qa-ok-bg);
  color: var(--qa-ok);
}

.tag.wa {
  background: var(--qa-wa-bg);
  color: var(--qa-wa);
}

.qq {
  font-size: 14px;
  line-height: 1.55;
  color: var(--qa-ink);
  margin: 0;
  overflow-wrap: anywhere;
}

.qvote {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 11px;
  border-radius: 99px;
  border: 1px solid var(--qa-line);
  background: #fff;
  font-size: 12px;
  font-weight: 600;
  color: var(--qa-mut);
  font-variant-numeric: tabular-nums;
  flex: 0 0 auto;
  transition: .13s;
}

.qvote svg {
  width: 13px;
  height: 13px;
}

.qvote:hover,
.qvote.on {
  border-color: var(--qa-pur-2);
  color: var(--qa-pur);
  background: var(--qa-tint);
}

.qvotes {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: var(--qa-faint);
  font-variant-numeric: tabular-nums;
}

/* Admin overflow menu */
.menu-dots {
  position: relative;
  flex: 0 0 auto;
}

.qmenu-btn {
  width: 28px;
  height: 28px;
  flex: 0 0 28px;
  border-radius: 8px;
  color: var(--qa-mut);
  border-color: transparent;
}

.qmenu-btn[aria-expanded="true"] {
  background: var(--qa-tint);
  color: var(--qa-pur);
}

.qmenu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 10;
  min-width: 148px;
  padding: 5px;
  background: #fff;
  border: 1px solid var(--qa-line);
  border-radius: 12px;
  box-shadow: 0 3px 10px rgba(28, 23, 32, .05), 0 10px 30px rgba(28, 23, 32, .06);
}

.qmenu-item {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  height: 34px;
  padding: 0 10px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: var(--qa-ink-2);
  text-align: left;
}

.qmenu-item:hover {
  background: var(--qa-tint);
  color: var(--qa-pur);
}

.qmenu-item.cr {
  color: var(--qa-cr);
}

.qmenu-item.cr:hover {
  background: #FDF2F2;
  color: var(--qa-cr);
}

/* Comments */
.qcomments-toggle {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 10px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--qa-pur);
}

.qcomments-toggle:hover {
  color: var(--qa-pur-2);
}

.chevron-icon {
  transition: transform .16s;
}

.chevron-icon.rotated {
  transform: rotate(180deg);
}

.qans {
  display: block;
  margin-top: 12px;
  padding: 10px 12px;
  background: var(--qa-paper);
  border: none;
  border-left: 3px solid var(--qa-pur-2);
  border-radius: 0 10px 10px 0;
}

.qans-head,
.qform-head {
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
}

.qans-face {
  width: 20px;
  height: 20px;
  font-size: 7px;
}

.qans b,
.qform-head b {
  display: block;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: .04em;
  text-transform: uppercase;
  color: var(--qa-pur);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.qans-time {
  margin-left: auto;
  flex: 0 0 auto;
  font-size: 11px;
  color: var(--qa-faint);
  font-variant-numeric: tabular-nums;
}

.qans p {
  font-size: 13.5px;
  line-height: 1.55;
  color: var(--qa-ink-2);
  margin: 4px 0 0;
  overflow-wrap: anywhere;
}

.qans p.live-answer {
  font-style: italic;
  color: var(--qa-mut);
}

.qform-wrap {
  margin-top: 12px;
}

.qform {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}

.inp {
  width: 100%;
  height: 42px;
  border: 1px solid var(--qa-line);
  border-radius: 11px;
  padding: 0 14px;
  font: inherit;
  font-size: 14.5px;
  color: var(--qa-ink);
  background: #fff;
}

.inp::placeholder {
  color: var(--qa-faint);
}

.inp:focus {
  outline: none;
  border-color: var(--qa-pur);
  box-shadow: 0 0 0 3px rgba(94, 47, 120, .1);
}

.qform .inp {
  flex: 1;
  min-width: 0;
  height: 36px;
  font-size: 13px;
}

.rnd {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: var(--qa-tint);
  color: var(--qa-pur);
  flex: 0 0 auto;
}

.rnd.p {
  background: linear-gradient(155deg, var(--qa-pur-2), var(--qa-pur));
  color: #fff;
}

.qform .rnd {
  width: 36px;
  height: 36px;
}

.qform .rnd:disabled {
  opacity: .5;
  cursor: not-allowed;
}

/* Admin actions */
.qact {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid var(--qa-line-2);
  flex-wrap: wrap;
}

.qcard .qact :deep(.xl-btn) {
  flex: 0 0 auto;
  font-size: 12.5px;
  height: 32px;
  padding: 0 12px;
}

/* ---------- Composer (spec 3.4) ---------- */
.qcomp {
  flex-shrink: 0;
  border-top: 1px solid var(--qa-line);
  padding: 11px 14px;
  background: #fff;
}

/* The app asks through a dialog, so the composer bar is one button drawn as
   the prototype's .rmsend input plus its round send button. */
.qask {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  text-align: left;
}

.qask-label {
  flex: 1;
  min-width: 0;
  height: 36px;
  display: flex;
  align-items: center;
  padding: 0 14px;
  border-radius: 99px;
  border: 1px solid var(--qa-line);
  background: #fff;
  font-size: 13px;
  color: var(--qa-mut);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: border-color .13s, box-shadow .13s;
}

.qask:hover .qask-label {
  border-color: var(--qa-pur-2);
}

.qask:hover .rnd.p {
  box-shadow: 0 5px 16px rgba(94, 47, 120, .26);
}

/* ---------- Loader ---------- */
.widget-loader {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.shimmer {
  background: linear-gradient(90deg, var(--qa-line-2) 25%, var(--qa-line) 50%, var(--qa-line-2) 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 8px;
}

.header-shimmer {
  height: 40px;
  width: 60%;
}

.tabs-shimmer {
  height: 32px;
  width: 100%;
}

.card-shimmer {
  height: 80px;
  width: 100%;
  border-radius: 14px;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* ---------- Tooltip content ---------- */
.qna-tooltip-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 240px;
  padding: 4px 0;
}

.qna-tooltip-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.qna-tooltip-row .status-text {
  font-weight: 700;
  color: #fff;
  font-size: 14px;
}

.qna-tooltip-sub {
  font-style: italic;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  padding-left: 26px; /* 16px icon + 10px gap */
}

/* ---------- Ask / answering overlays (spec 8 .mod / .mdl.ses) ---------- */
.mod {
  position: absolute;
  inset: 0;
  z-index: 100;
  background: rgba(28, 23, 32, .44);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px;
}

.mdl {
  background: #fff;
  border-radius: 22px;
  width: 100%;
  max-width: 560px;
  max-height: 86vh;
  max-height: calc(100% - 8px);
  display: flex;
  flex-direction: column;
  box-shadow: 0 30px 80px rgba(28, 23, 32, .3);
  overflow: hidden;
  animation: qa-mdl-in .2s ease-out;
}

.mdl.ses {
  max-width: 440px;
}

.mdl .mh {
  padding: 24px 26px 14px;
  border-bottom: 1px solid var(--qa-line-2);
  flex-shrink: 0;
}

.mdl .mh h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  letter-spacing: -.02em;
  color: var(--qa-ink);
}

.sesmark {
  width: 46px;
  height: 46px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  background: var(--qa-tint);
  color: var(--qa-pur);
  margin-bottom: 14px;
}

.mdl .mb {
  padding: 16px 26px 20px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.mdl .mf {
  padding: 16px 26px;
  border-top: 1px solid var(--qa-line-2);
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: flex-end;
  background: var(--qa-paper);
  flex-shrink: 0;
}

.mdl .mf :deep(.xl-btn) {
  white-space: nowrap;
}

.inp.area {
  height: auto;
  min-height: 108px;
  padding: 11px 14px;
  font-size: 14px;
  line-height: 1.55;
  resize: vertical;
}

.mod-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: -4px;
}

.char-count {
  font-size: 11.5px;
  color: var(--qa-faint);
  font-variant-numeric: tabular-nums;
}

.qanon {
  display: flex;
  align-items: center;
}

.qanon .qanon-switch {
  gap: 8px;
}

.qanon .qanon-switch :deep(.xl-switch__text b) {
  font-size: 12px;
  font-weight: 500;
  color: var(--qa-mut);
}

.privacy-note,
.privacy-status {
  font-size: 12px;
  color: var(--qa-mut);
}

.privacy-status {
  margin-top: -6px;
}

.qcard.preview {
  margin-bottom: 0;
  box-shadow: none;
  background: var(--qa-paper);
}

.sesfacts {
  margin: 0;
  background: var(--qa-paper);
  border: 1px solid var(--qa-line);
  border-radius: 14px;
  padding: 13px 15px;
  font-size: 13.5px;
  color: var(--qa-ink-2);
  line-height: 1.65;
}

.text-answer-area {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

@keyframes qa-mdl-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .mdl,
  .shimmer {
    animation: none;
  }

  .qvote,
  .ib,
  .chevron-icon,
  .qask-label,
  .rmsub .seg button {
    transition: none;
  }
}

/* ---------- Q&A on a phone (prototype sheetQA, XtendLive_Mobile_Room.html) ---------- */
.qna-phone { display: flex; flex: 1; flex-direction: column; min-height: 0; height: 100%; color: #1C1720; font-family: var(--xl-font-family); }
.qp-host { padding: 2px 16px 10px; }
.qp-body { flex: 1; min-height: 0; overflow-y: auto; padding: 0 16px 12px; }

/* prototype .seg */
.qp-seg { display: flex; gap: 4px; margin: 2px 0 12px; padding: 3px; border-radius: 99px; background: #F3EFF5; }
.qp-seg button { display: inline-flex; flex: 1; align-items: center; justify-content: center; gap: 6px; height: 32px; border: 0; border-radius: 99px; background: none; color: #514A57; font: inherit; font-size: 13px; font-weight: 600; cursor: pointer; }
.qp-seg button.on { background: #fff; color: #1C1720; box-shadow: 0 1px 3px rgba(28, 23, 32, 0.1); }
.qp-seg i { font-style: normal; color: #867D8D; font-variant-numeric: tabular-nums; }

/* prototype .card / .qtop / .qq / .ans / .qfoot */
.qp-card { margin-bottom: 9px; padding: 12px 13px; border: 1px solid #ECE6EF; border-radius: 16px; background: #fff; }
.qp-top { display: flex; align-items: center; gap: 8px; margin-bottom: 7px; color: #867D8D; font-size: 12px; }
.qp-top b { color: #514A57; font-weight: 600; }
.qp-av { display: grid; flex: 0 0 24px; place-items: center; width: 24px; height: 24px; overflow: hidden; border-radius: 50%; background: #EADFF3; color: #2A0E3C; font-size: 9.5px; font-weight: 600; }
.qp-av img { width: 100%; height: 100%; object-fit: cover; }
.qp-av--anon { background: #E8E2EC; color: #867D8D; }
.qp-q { margin: 0 0 9px; font-size: 14px; line-height: 1.45; }
.qp-ans { margin: 0 0 9px; padding: 9px 11px; border-radius: 11px; background: #F4FAF6; color: #514A57; font-size: 13px; line-height: 1.45; }
.qp-ans b { display: block; margin-bottom: 2px; color: #166B48; font-size: 12px; }
.qp-foot { display: flex; align-items: center; gap: 6px; }
.qp-foot .sp { flex: 1; }

/* prototype .st */
.qp-st { display: inline-flex; align-items: center; gap: 4px; height: 22px; padding: 0 8px; border-radius: 99px; background: #F5EFF9; color: #5E2F78; font-size: 11.5px; font-weight: 600; white-space: nowrap; }
.qp-st--ok { background: #E4F3EB; color: #166B48; }
.qp-st--live { background: #FBF0F7; color: #9A2D6E; }
.qp-st--no { background: #F3F1F4; color: #867D8D; }

.qp-btn { height: 30px; padding: 0 12px; border: 1px solid #ECE6EF; border-radius: 99px; background: #fff; color: #1C1720; font: inherit; font-size: 12.5px; font-weight: 600; white-space: nowrap; cursor: pointer; }
.qp-ib { display: grid; place-items: center; width: 30px; height: 30px; padding: 0; border: 1px solid #ECE6EF; border-radius: 11px; background: transparent; color: #514A57; cursor: pointer; }

/* prototype .vote */
.qp-vote { display: inline-flex; align-items: center; gap: 4px; height: 30px; padding: 0 11px; border: 1px solid #ECE6EF; border-radius: 99px; background: #fff; color: #514A57; font: inherit; font-size: 12.5px; font-weight: 700; font-variant-numeric: tabular-nums; cursor: pointer; }
.qp-vote.on { border-color: #D9C6E8; background: #F5EFF9; color: #5E2F78; }
.qp-btn:focus-visible, .qp-ib:focus-visible, .qp-vote:focus-visible, .qp-seg button:focus-visible { outline: 2px solid #5E2F78; outline-offset: 2px; }

/* prototype .empty */
.qp-empty { padding: 30px 18px 26px; color: #867D8D; text-align: center; }
.qp-empty__g { display: grid; place-items: center; width: 52px; height: 52px; margin: 0 auto 12px; border-radius: 16px; background: #F5EFF9; color: #5E2F78; }
.qp-empty b { display: block; margin-bottom: 4px; color: #1C1720; font-size: 15px; }
.qp-empty p { max-width: 250px; margin: 0 auto; font-size: 13px; line-height: 1.5; }
.qp-note { padding: 20px 0; color: #867D8D; font-size: 13px; text-align: center; }

/* prototype .sfoot + .composer + .anon */
.qp-ask { flex: 0 0 auto; padding: 10px 12px calc(12px + env(safe-area-inset-bottom, 0px)); border-top: 1px solid #ECE6EF; background: #fff; }
.qp-composer { display: flex; align-items: center; gap: 8px; }
/* At least 16px, or an iPhone zooms the page when the box is tapped. */
.qp-composer input { flex: 1; min-width: 0; height: 42px; padding: 0 16px; border: 1px solid #E2DBE6; border-radius: 99px; outline: none; background: #FAF8FB; color: #1C1720; font: inherit; font-size: 16px; }
.qp-composer input:focus { border-color: #8154A6; background: #fff; }
.qp-send { display: grid; flex: 0 0 42px; place-items: center; width: 42px; height: 42px; padding: 0; border: 0; border-radius: 50%; background: linear-gradient(155deg, #8154A6, #5E2F78); color: #fff; cursor: pointer; }
.qp-send:disabled { opacity: 0.5; cursor: default; }
.qp-anon { margin-top: 9px; font-size: 12.5px; color: #514A57; }
.qp-closed { margin: 2px 0; color: #867D8D; font-size: 13px; text-align: center; }
</style>
